import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Grid,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ApiCallService from "../../../Service/ApiCallService";
import NecFollowUpSummaryCards from "./NecFollowUpSummaryCards";
import NecFollowUpTable from "./NecFollowUpTable";
import NecMemberDetailsDrawer from "./NecMemberDetailsDrawer";
import { followUpCategoryFilterOptions } from "./followUpCategories";
const queryStatuses = ["", "Completed", "Pending", "On Hold", "NA"];
const callTypeOptions = [
  { label: "All", value: "" },
  { label: "Call", value: "CALL" },
  { label: "Message", value: "MESSAGE" },
  { label: "Group Call", value: "GROUP" },
  { label: "In Person Meet", value: "IN_PERSON_MEET" },
];

const getCallTypeFilterLabel = (value) =>
  callTypeOptions.find((item) => item.value === value)?.label || "All";

const monthOptions = [
  { label: "All", value: "" },
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

const currentDate = new Date();

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildSummaryFromMembers = (rows = []) =>
  rows.reduce(
    (acc, row) => {
      acc.totalFollowUps += row.totalFollowUps || 0;
      acc.outgoingCalls += row.outgoingCalls || 0;
      acc.incomingCalls += row.incomingCalls || 0;
      acc.groupCalls += row.groupCalls || 0;
      acc.queries += row.queries || 0;
      acc.queriesReceived += row.queriesReceived || 0;
      acc.completed += row.completed || 0;
      acc.resolved += row.resolved || 0;
      acc.pending += row.pending || 0;
      acc.onHold += row.onHold || 0;
      acc.na += row.na || 0;
      return acc;
    },
    {
      totalFollowUps: 0,
      outgoingCalls: 0,
      incomingCalls: 0,
      groupCalls: 0,
      queries: 0,
      queriesReceived: 0,
      completed: 0,
      resolved: 0,
      pending: 0,
      onHold: 0,
      na: 0,
    },
  );

const filterInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    bgcolor: "#f8fafc",
    height: 32,
  },
  "& .MuiInputBase-input": {
    fontSize: 14,
    py: 0.6,
  },
};

const FilterLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      mb: 0.4,
      color: "#6f5b4f",
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: 0.8,
    }}
  >
    {children}
  </Typography>
);

const allSelectProps = {
  displayEmpty: true,
  renderValue: (selected) => selected || "All",
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return getId(value._id);
  }
  return "";
};

const getDesignationText = (member) => {
  if (Array.isArray(member?.designations)) {
    return member.designations
      .map((designation) => designation?.title || designation?.name || "")
      .filter(Boolean)
      .join(", ");
  }

  return member?.position || member?.designation || "";
};

const getMembersFromResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.members)) return response.members;
  return [];
};

const getTimeStamp = (value) => {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRecordsByCreatedAt = (records = []) =>
  [...records].sort((left, right) => {
    const leftTime = getTimeStamp(left?.createdAt);
    const rightTime = getTimeStamp(right?.createdAt);

    return rightTime - leftTime;
  });

const getLatestCreatedAt = (member = {}) => {
  const recordTimes = (member?.records || [])
    .map((record) => getTimeStamp(record?.createdAt))
    .filter(Boolean);

  if (recordTimes.length) {
    return Math.max(...recordTimes);
  }

  return getTimeStamp(member?.createdAt);
};

const sortMembersByCreatedAt = (members = []) =>
  [...members].sort((left, right) => {
    const leftTime = getLatestCreatedAt(left);
    const rightTime = getLatestCreatedAt(right);

    return rightTime - leftTime;
  });

const committeeSections = [
  {
    key: "governing",
    title: "Governing Committee",
    aliases: ["governing committee", "governing"],
  },
  {
    key: "operations",
    title: "Operations Committee",
    aliases: ["operations committee", "operation committee", "operations"],
  },
  {
    key: "nationalProgramHead",
    title: "National Program Head",
    aliases: ["national program head", "national programme head"],
  },
  {
    key: "stateLeaders",
    title: "State Presidents & State General Secretaries",
    aliases: [
      "state presidents state general secretaries",
      "state president state general secretary",
      "state presidents",
      "state general secretaries",
      "state president",
      "state general secretary",
    ],
  },
];

const normalizeText = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getMemberSectionKey = (member) => {
  const text = normalizeText(
    [member.committeeType, member.position, member.council]
      .filter(Boolean)
      .join(" "),
  );

  const section = committeeSections.find(({ aliases }) =>
    aliases.some((alias) => text.includes(normalizeText(alias))),
  );

  return section?.key || "other";
};

const normalizeCommitteeMember = (member) => {
  const user = member?.user || member?.userId || member;
  const committee = member?.committee || {};
  const committeeType = committee?.committeeType || member?.committeeType || {};
  const council = committeeType?.councilId || member?.council || {};

  return {
    memberId: getId(user?._id) || getId(member?.userId) || getId(member?._id),
    position: getDesignationText(member),
    committeeType:
      committeeType?.name ||
      member?.committeeType?.name ||
      member?.committeeType ||
      "",
    council:
      council?.councilName ||
      member?.council?.councilName ||
      member?.council ||
      "",
  };
};

const buildCommitteeGroups = (items = []) => {
  const groups = committeeSections.map((section) => ({
    ...section,
    memberIds: [],
  }));
  const otherGroup = {
    key: "other",
    title: "Other Committee Members",
    memberIds: [],
  };

  items.forEach((item) => {
    const sectionKey = getMemberSectionKey(item);
    const group = groups.find(({ key }) => key === sectionKey) || otherGroup;
    if (item.memberId) group.memberIds.push(item.memberId);
  });

  return [...groups, otherGroup]
    .map((group) => ({
      title: group.title,
      memberIds: Array.from(new Set(group.memberIds)),
    }))
    .filter((group) => group.memberIds.length > 0);
};

const NecFollowUpPage = () => {
  const [filters, setFilters] = useState({
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
    memberId: "",
    category: "",
    callType: "",
    queryStatus: "",
    createdBy: [],
    search: "",
  });
  const [members, setMembers] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [allDetails, setAllDetails] = useState(null);
  const [committeeGroups, setCommitteeGroups] = useState([]);
  const getMonthParam = useCallback(
    () => (filters.month ? filters.month : undefined),
    [filters.month],
  );

  const fetchCommitteeGroups = useCallback(async () => {
    try {
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/committeeMembers/membersByCouncil?councilId=69304eb9083479c0cf0dc0e3`,
      );
      const committeeMembers = getMembersFromResponse(response).map(
        normalizeCommitteeMember,
      );
      setCommitteeGroups(buildCommitteeGroups(committeeMembers));
    } catch (err) {
      console.error("Failed to load committee groups", err);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/followUp/nec-summary`,
        {
          month: getMonthParam(),
          year: filters.year,
          memberId: filters.memberId,
          category: filters.category,
          callType: filters.callType,
          queryStatus: filters.queryStatus,
          createdBy: filters.createdBy.length
            ? filters.createdBy.join(",")
            : undefined,
        },
      );
      const membersFromResponse = response?.data?.members || [];
      setAllDetails(response?.data);
      setMembers(sortMembersByCreatedAt(membersFromResponse));
      setCreators(response?.data?.creators || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to load NEC Touch-point summary",
      );
    } finally {
      setLoading(false);
    }
  }, [
    filters.year,
    filters.memberId,
    filters.category,
    filters.callType,
    filters.queryStatus,
    filters.createdBy,
    getMonthParam,
  ]);

  const fetchMemberDetails = useCallback(
    async (member = selectedMember) => {
      if (!member?.memberId) return;
      try {
        setDetailsLoading(true);
        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/followUp/nec-member/${member.memberId}`,
          {
            month: getMonthParam(),
            year: filters.year,
            category: filters.category,
            callType: filters.callType,
            createdBy: filters.createdBy.length
              ? filters.createdBy.join(",")
              : undefined,
          },
        );
        const details = response?.data || null;
        setMemberDetails(
          details
            ? {
                ...details,
                records: sortRecordsByCreatedAt(details.records || []),
              }
            : null,
        );
      } catch (err) {
        setError(
          err?.response?.data?.message || "Failed to load member details",
        );
      } finally {
        setDetailsLoading(false);
      }
    },
    [
      filters.year,
      filters.category,
      filters.callType,
      filters.createdBy,
      selectedMember,
      getMonthParam,
    ],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchCommitteeGroups();
  }, [fetchCommitteeGroups]);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const clearFilters = () => {
    setFilters({
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
      memberId: "",
      category: "",
      callType: "",
      queryStatus: "",
      createdBy: [],
      search: "",
    });
  };

  const handleViewDetails = async (member) => {
    setSelectedMember(member);

    if (member?.isGroupCall) {
      setMemberDetails({
        member,
        records: sortRecordsByCreatedAt(member.records || []),
      });
      return;
    }

    await fetchMemberDetails(member);
  };

  const refreshAll = async () => {
    await fetchSummary();
    if (selectedMember) await fetchMemberDetails(selectedMember);
  };

  const filteredMembers = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((member) =>
      [member.memberName, member.mobile, member.designation, member.region]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [members, filters.search]);

  const displaySummary = useMemo(
    () => buildSummaryFromMembers(filteredMembers),
    [filteredMembers],
  );

  const years = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    return Array.from({ length: 7 }, (_, index) => currentYear - 3 + index);
  }, []);

  const exportReport = () => {
    const monthName =
      monthOptions.find((month) => month.value === filters.month)?.label ||
      "All";
    const selectedMemberName =
      members.find((member) => member.memberId === filters.memberId)
        ?.memberName || "All Members";
    const selectedCreatorNames = filters.createdBy.length
      ? creators
          .filter((creator) => filters.createdBy.includes(creator.userId))
          .map((creator) => creator.name)
          .join(", ") || "All"
      : "All";
    const rows = filteredMembers
      .map(
        (member) => `
          <tr>
            <td>${escapeHtml(member.memberName)}</td>
            <td>${escapeHtml(member.designation || "NEC Member")}</td>
            <td>${escapeHtml(member.region)}</td>
            <td class="number">${member.outgoingCalls || 0}</td>
            <td class="number">${member.incomingCalls || 0}</td>
            <td class="number">${member.totalFollowUps || 0}</td>
            <td class="number">${member.queries || 0}</td>
            <td class="number">${member.completed || 0}</td>
            <td class="number">${member.pending || 0}</td>
            <td class="number">${member.onHold || 0}</td>
            <td class="number">${member.na || 0}</td>
          </tr>`,
      )
      .join("");

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; }
            .title { font-size: 20px; font-weight: 700; color: #1f2937; }
            .meta { color: #475569; font-size: 12px; }
            th { background: #A92FDD; color: #ffffff; font-weight: 700; border: 1px solid #7e22ce; padding: 8px; }
            td { border: 1px solid #cbd5e1; padding: 7px; vertical-align: top; }
            .number { text-align: center; mso-number-format: "0"; }
            .summary { background: #f8fafc; font-weight: 700; }
          </style>
        </head>
        <body>
          <table>
            <tr><td colspan="13" class="title">NEC Touch-point Management</td></tr>
            <tr><td colspan="13" class="meta">Month: ${escapeHtml(monthName)} ${escapeHtml(filters.year)} | NEC Member: ${escapeHtml(selectedMemberName)} | Category: ${escapeHtml(filters.category || "All")} | Call Type: ${escapeHtml(callTypeOptions.find((item) => item.value === filters.callType)?.label || "All")} | Status: ${escapeHtml(filters.queryStatus || "All")} | Created By: ${escapeHtml(selectedCreatorNames)} | Search: ${escapeHtml(filters.search || "-")}</td></tr>
            <tr></tr>
            <tr class="summary">
              <td>Total Touch-points</td><td>${displaySummary.totalFollowUps}</td>
              <td>Outgoing Calls</td><td>${displaySummary.outgoingCalls}</td>
              <td>Incoming Calls</td><td>${displaySummary.incomingCalls}</td>
              <td>Queries</td><td>${displaySummary.queries}</td>
              <td>Completed</td><td>${displaySummary.completed}</td>
            </tr>
            <tr></tr>
            <tr>
              <th>NEC Member</th>
              <th>Designation</th>
              <th>Region/State</th>
              <th>Outgoing Calls</th>
              <th>Incoming Calls</th>
              <th>Total Touch-points</th>
              <th>Queries</th>
              <th>Completed</th>
              <th>Pending</th>
              <th>On Hold</th>
              <th>NA</th>
            </tr>
            ${rows || '<tr><td colspan="13">No data available</td></tr>'}
          </table>
        </body>
      </html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `nec-touch-point-${filters.year}-${filters.month ? String(filters.month).padStart(2, "0") : "all"}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f8fb", p: { xs: 1.5, md: 3 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#07111f",
              fontSize: { xs: 28, md: 31 },
              lineHeight: 1.1,
              fontWeight: 900,
            }}
          >
            Touch-point Management
          </Typography>
          <Typography sx={{ mt: 0.5, color: "#7a6b63", fontSize: 15 }}>
            Strategic performance monitoring and query resolution dashboard for
            NEC members.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={exportReport}
            variant="contained"
            disabled={loading}
            sx={{
              px: 2.3,
              py: 1.1,
              textTransform: "none",
              borderRadius: 1,
              bgcolor: "#A7CBEF",
              fontWeight: 900,
              boxShadow: "none",
              "&:hover": { bgcolor: "#2e8be9", boxShadow: "none" },
            }}
          >
            Export Report
          </Button>
        </Stack>
      </Stack>

      <Card
        sx={{
          p: 2,
          mb: 3.6,
          border: "1px solid #dbe5ef",
          borderRadius: 1.2,
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          bgcolor: "#fff",
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={1.8}>
            <FilterLabel>Timeline</FilterLabel>
            <Stack direction="row" spacing={0.7}>
              {/* <TextField select fullWidth size="small" value={filters.month} onChange={handleFilterChange("month")} sx={filterInputSx}>
                {monthOptions.map((label, index) => (
                  <MenuItem key={label} value={index + 1}>{label}</MenuItem> 
                ))}
              </TextField> */}

              <TextField
                select
                fullWidth
                size="small"
                value={filters.month}
                onChange={handleFilterChange("month")}
                sx={filterInputSx}
              >
                {monthOptions.map((m) => (
                  <MenuItem key={m.label} value={m.value || "All"}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                value={filters.year}
                onChange={handleFilterChange("year")}
                sx={filterInputSx}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Grid>
          <Grid item xs={12} md={2.1}>
            <FilterLabel>Member</FilterLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name..."
              value={filters.search}
              onChange={handleFilterChange("search")}
              sx={filterInputSx}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FilterLabel>Category</FilterLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={filters.category}
              onChange={handleFilterChange("category")}
              SelectProps={allSelectProps}
              sx={filterInputSx}
            >
              {followUpCategoryFilterOptions.map((item) => (
                <MenuItem key={item || "all"} value={item}>
                  {item || "All"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.3}>
            <FilterLabel>Type</FilterLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={filters.callType}
              onChange={handleFilterChange("callType")}
              SelectProps={{
                ...allSelectProps,
                renderValue: getCallTypeFilterLabel,
              }}
              sx={filterInputSx}
            >
              {callTypeOptions.map((item) => (
                <MenuItem key={item.value || "all"} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.3}>
            <FilterLabel>Status</FilterLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={filters.queryStatus}
              onChange={handleFilterChange("queryStatus")}
              SelectProps={allSelectProps}
              sx={filterInputSx}
            >
              {queryStatuses.map((item) => (
                <MenuItem key={item || "all"} value={item}>
                  {item || "All"}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.6}>
            <FilterLabel>Initiated By</FilterLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={filters.createdBy}
              onChange={handleFilterChange("createdBy")}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected.length) return "All";

                  return creators
                    .filter((creator) => selected.includes(creator.userId))
                    .map((creator) => creator.name)
                    .join(", ");
                },
              }}
              sx={filterInputSx}
            >
              {creators.map((creator) => (
                <MenuItem key={creator.userId} value={creator.userId}>
                  <Checkbox
                    size="small"
                    checked={filters.createdBy.includes(creator.userId)}
                  />
                  <ListItemText primary={creator.name} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.2}>
            <Button
              onClick={clearFilters}
              sx={{
                height: 32,
                color: "#a44900",
                fontSize: 13,
                fontWeight: 900,
                textTransform: "none",
                whiteSpace: "nowrap",
                border: "1px solid #a44900",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "#a44900",
                  color: "#fff",
                  border: "1px solid #a44900",
                },
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2.5 }}>
        <NecFollowUpSummaryCards
          summary={displaySummary}
          category={filters.category}
          counts={allDetails}
        />
      </Box>

      <NecFollowUpTable
        members={filteredMembers}
        loading={loading}
        committeeGroups={committeeGroups}
        onViewDetails={handleViewDetails}
      />

      <NecMemberDetailsDrawer
        open={Boolean(selectedMember)}
        member={selectedMember}
        details={memberDetails}
        //loading={detailsLoading}
        month={filters.month}
        year={filters.year}
        committeeGroups={committeeGroups}
        onClose={() => {
          setSelectedMember(null);
          setMemberDetails(null);
        }}
        onRefresh={refreshAll}
      />
    </Box>
  );
};

export default NecFollowUpPage;
